import React, { useEffect, useState } from 'react';
import { BarChart3, Download, Filter, Eye, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import FormPageShell from '../components/Common/FormPageShell';
import PageHeader from '../components/Common/PageHeader';
import Can from '../components/Common/Can';
import { getAppHomePath } from '../constants/navigation';
import { P } from '../constants/permissions';
import { licenseService, identityService } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../contexts/TenantContext';
import { tenantBrand } from '../utils/tenantBranding';
import { LICENCA_STATUS_LABELS } from '../utils/licenseStatus';
import {
  fetchReportDataset,
  filterLicenseDataset,
  buildLicensesReportHtml,
  buildEnterprisesReportHtml,
  buildExpiringReportHtml,
  previewReportHtml,
  downloadReportPdf,
  downloadReportXlsx,
  reportErrorMessage,
} from '../utils/reportBuilder';

const reportCards = [
  {
    id: 'licenses',
    title: 'Relatório de licenças',
    subtitle: 'Status e vencimentos',
    body: 'Distribuição por status, classes de vencimento e tabela analítica (amostra).',
    iconWrap: 'bg-primary-100 text-primary-600',
    fileBase: 'relatorio-licencas',
  },
  {
    id: 'enterprises',
    title: 'Relatório de empreendimentos',
    subtitle: 'Cadastros e atividades',
    body: 'Tipos de empreendimento, contagens e licenças vinculadas por unidade.',
    iconWrap: 'bg-success-100 text-success-600',
    fileBase: 'relatorio-empreendimentos',
  },
  {
    id: 'expiring',
    title: 'Relatório de vencimentos',
    subtitle: 'Licenças próximas do vencimento',
    body: 'Lista focada na janela de 30 dias e total de vencidas.',
    iconWrap: 'bg-warning-100 text-warning-600',
    fileBase: 'relatorio-vencimentos',
  },
];

const Reports = () => {
  const { t } = useTranslation('reports');
  const { tenant } = useTenant();
  const brand = { ...tenantBrand(tenant), orgName: tenant?.name || 'useAmbiental' };

  const [busy, setBusy] = useState(false);
  const [builderFormat, setBuilderFormat] = useState('pdf');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeDetails, setIncludeDetails] = useState(true);
  const [period, setPeriod] = useState('');
  const [tipoId, setTipoId] = useState('');
  const [status, setStatus] = useState('');
  const [tipos, setTipos] = useState([]);

  useEffect(() => {
    let active = true;
    licenseService
      .getTiposLicenca({ skip: 0, limit: 200 })
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        if (active) setTipos(rows);
      })
      .catch(() => {
        if (active) setTipos([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const builderFilters = { period, tipoId, status };

  const runReport = async (kind, { preview, downloadPdf, downloadXlsx }) => {
    setBusy(true);
    try {
      const dataset = await fetchReportDataset({ licenseService, identityService });
      let html;
      if (kind === 'licenses') html = buildLicensesReportHtml(dataset, brand);
      else if (kind === 'enterprises') html = buildEnterprisesReportHtml(dataset, brand);
      else html = buildExpiringReportHtml(dataset, brand);

      const card = reportCards.find((c) => c.id === kind);
      const base = card?.fileBase || 'relatorio';

      if (preview) previewReportHtml(html);
      if (downloadPdf) await downloadReportPdf(html, base);
      if (downloadXlsx) downloadReportXlsx(dataset, kind, base);

      if (preview) toast.success(t('htmlOpened'));
      else if (downloadPdf) toast.success(t('pdfGenerated'));
      else if (downloadXlsx) toast.success(t('spreadsheetGenerated'));
    } catch (e) {
      toast.error(reportErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const handleBuilderGenerate = async (mode) => {
    setBusy(true);
    try {
      const dataset = filterLicenseDataset(
        await fetchReportDataset({ licenseService, identityService }),
        builderFilters
      );
      if (mode === 'preview') {
        const html = buildLicensesReportHtml(dataset, brand);
        previewReportHtml(html);
        toast.success(t('htmlOpened'));
        return;
      }
      if (builderFormat === 'pdf') {
        const html = buildLicensesReportHtml(dataset, brand);
        await downloadReportPdf(html, 'relatorio-personalizado');
        toast.success(t('pdfGenerated'));
      } else {
        downloadReportXlsx(dataset, 'licenses', 'relatorio-personalizado');
        toast.success(t('spreadsheetGenerated'));
      }
    } catch (e) {
      toast.error(reportErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("title")}
          description="Visualize o relatório em HTML na tela. Para arquivo, baixe apenas PDF ou planilha Excel (.xlsx)."
          breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Relatórios' }]}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reportCards.map((card) => (
            <div key={card.title} className="data-list-card flex flex-col">
              <div className="flex flex-1 flex-col p-5 sm:p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${card.iconWrap}`}
                  >
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{card.title}</h3>
                    <p className="text-sm text-gray-600">{card.subtitle}</p>
                  </div>
                </div>
                <p className="mb-4 flex-1 text-sm text-gray-700">{card.body}</p>
                <div className="flex flex-wrap gap-2">
                  <Can permission={P.REPORTS_VIEW}>
                    <button
                      type="button"
                      disabled={busy}
                      className="btn btn-primary btn-sm flex-1 min-w-[6.5rem]"
                      onClick={() =>
                        runReport(card.id, {
                          preview: false,
                          downloadPdf: true,
                          downloadXlsx: false,
                        })
                      }
                    >
                      <Download className="mr-1 h-4 w-4" />
                      PDF
                    </button>
                  </Can>
                  <Can permission={P.REPORTS_VIEW}>
                    <button
                      type="button"
                      disabled={busy}
                      className="btn btn-primary btn-sm flex-1 min-w-[6.5rem]"
                      onClick={() =>
                        runReport(card.id, {
                          preview: false,
                          downloadPdf: false,
                          downloadXlsx: true,
                        })
                      }
                    >
                      <FileSpreadsheet className="mr-1 h-4 w-4" />
                      Excel
                    </button>
                  </Can>
                  <Can permission={P.REPORTS_VIEW}>
                    <button
                      type="button"
                      disabled={busy}
                      className="btn btn-outline btn-sm flex-1 min-w-[6.5rem]"
                      onClick={() =>
                        runReport(card.id, {
                          preview: true,
                          downloadPdf: false,
                          downloadXlsx: false,
                        })
                      }
                    >
                      <Eye className="mr-1 h-4 w-4" />
                      Ver HTML
                    </button>
                  </Can>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="data-list-card">
          <div className="flex items-center gap-2 border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <BarChart3 className="h-5 w-5 text-primary-700" aria-hidden />
            <div>
              <h2 className="text-base font-semibold tracking-tight text-gray-900">
                Construtor de relatórios
              </h2>
              <p className="text-xs text-gray-500">
                Pré-visualização em HTML. Download apenas em PDF ou planilha (relatório de licenças).
              </p>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <h4 className="mb-4 text-sm font-semibold text-gray-900">Filtros</h4>
                <div className="form-stack">
                  <div className="form-group">
                    <label className="form-label">Período (referência)</label>
                    <select className="form-select" value={period} onChange={(e) => setPeriod(e.target.value)}>
                      <option value="">Base completa</option>
                      <option value="last-month">Último mês</option>
                      <option value="last-quarter">Último trimestre</option>
                      <option value="last-year">Último ano</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tipo de licença</label>
                    <select className="form-select" value={tipoId} onChange={(e) => setTipoId(e.target.value)}>
                      <option value="">Todos</option>
                      {tipos.map((tipo) => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.nome || tipo.name || tipo.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="">Todos</option>
                      {Object.entries(LICENCA_STATUS_LABELS).map(([id, label]) => (
                        <option key={id} value={id}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="mb-4 text-sm font-semibold text-gray-900">Opções de exportação</h4>
                <div className="form-stack">
                  <div className="form-group">
                    <label className="form-label">Formato do arquivo</label>
                    <select
                      className="form-select"
                      value={builderFormat}
                      onChange={(e) => setBuilderFormat(e.target.value)}
                    >
                      <option value="pdf">PDF</option>
                      <option value="xlsx">Excel (.xlsx)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Incluir gráficos</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="form-check"
                        checked={includeCharts}
                        onChange={(e) => setIncludeCharts(e.target.checked)}
                      />
                      <span className="text-sm text-gray-700">Sim</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Incluir dados detalhados</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="form-check"
                        checked={includeDetails}
                        onChange={(e) => setIncludeDetails(e.target.checked)}
                      />
                      <span className="text-sm text-gray-700">Sim</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-6">
              <Can permission={P.REPORTS_VIEW}>
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={busy}
                  onClick={() => handleBuilderGenerate('preview')}
                >
                  <Filter className="mr-2 h-4 w-4" />
                  Ver HTML
                </button>
              </Can>
              <Can permission={P.REPORTS_VIEW}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => handleBuilderGenerate('download')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {builderFormat === 'pdf' ? 'Baixar PDF' : 'Baixar Excel'}
                </button>
              </Can>
            </div>
          </div>
        </div>
      </div>
    </FormPageShell>
  );
};

export default Reports;
